<script setup lang="ts">
import { $dfs, mergeRegister } from "@lexical/utils";
import { $getNodeByKey, $getRoot, COMMAND_PRIORITY_NORMAL, KEY_BACKSPACE_COMMAND } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";

import { $isDartLinkNode, DartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { ENTITY_KIND_URL } from "~/constants/kind";
import { isDefined } from "~/shared/common";
import type { ChatExtraItem, LinkUpdate } from "~/shared/types";
import { useDataStore } from "~/stores";
import { isValidUrlIncludingLocal } from "~/utils/validation";

const props = defineProps<{
  chatDuid: string;
  isEmpty: boolean;
}>();

const dataStore = useDataStore();
const editor = useLexicalComposer();

const nodeKeyToDuid = ref<Map<string, string>>(new Map());
const draft = computed(() => dataStore.getChatByDuid(props.chatDuid)?.draft);

editor.read(() =>
  $dfs($getRoot()).forEach(({ node }) => {
    if (!$isDartLinkNode(node)) {
      return;
    }
    const duid = node.getDuid();
    if (!duid) {
      return;
    }
    nodeKeyToDuid.value.set(node.getKey(), duid);
  })
);

const handleMutations = (mutatedNodes: Map<string, string>) =>
  nextTick(() =>
    editor.update(() => {
      const mutatedNodeEntries = [...mutatedNodes.entries()];

      mutatedNodeEntries.forEach(([key, action]) => {
        if (!draft.value) {
          return;
        }

        if (action === "destroyed") {
          const duid = nodeKeyToDuid.value.get(key);
          dataStore.updateChat({
            duid: props.chatDuid,
            draft: {
              ...draft.value,
              extras: draft.value.extras.filter((e) => e.duid !== duid),
            },
          });
          return;
        }

        const node = $getNodeByKey(key);
        if (!node || !$isDartLinkNode(node)) {
          return;
        }

        const kind = node.getKind();
        const duid = node.getDuid();
        if (!duid) {
          return;
        }

        nodeKeyToDuid.value.set(key, duid);
        const entity = draft.value.extras.find((e) => e.duid === duid);
        if (entity) {
          return;
        }

        // Node was created
        dataStore.updateChat({
          duid: props.chatDuid,
          draft: {
            ...draft.value,
            extras: [...draft.value.extras, { duid, kind }],
          },
        });
      });
    })
  );

let unregisterListeners: () => void;

const onBackspace = (event: KeyboardEvent) => {
  if (!props.isEmpty || !draft.value) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();

  dataStore.updateChat({
    duid: props.chatDuid,
    draft: {
      ...draft.value,
      extras: draft.value.extras.slice(0, -1),
    },
  });

  return true;
};

const handleLinkChanges = (creates: string[], destroys: string[], updates: LinkUpdate[]) => {
  if (!draft.value) {
    return;
  }

  const chatUrlSet = new Set(draft.value.extras.map((e) => e.url).filter(isDefined));

  const linksToCreate = creates;
  const linksToDestroy = destroys;
  updates.forEach((update) => {
    linksToCreate.push(update.new);
    linksToDestroy.push(update.old);
  });

  linksToDestroy.forEach((destroy) => chatUrlSet.delete(destroy));
  linksToCreate.forEach((create) => {
    if (!isValidUrlIncludingLocal(create)) {
      return;
    }
    chatUrlSet.add(create);
  });

  const extrasNoUrl = draft.value.extras.filter((e) => !isDefined(e.url));
  const extrasWithUrl: ChatExtraItem[] = [...chatUrlSet].map((url) => ({
    kind: ENTITY_KIND_URL,
    url,
  }));

  dataStore.updateChat({
    duid: props.chatDuid,
    draft: { ...draft.value, extras: [...extrasNoUrl, ...extrasWithUrl] },
  });
};

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand(KEY_BACKSPACE_COMMAND, onBackspace, COMMAND_PRIORITY_NORMAL),
    editor.registerMutationListener(DartLinkNode, handleMutations)
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});

defineExpose({
  handleLinkChanges,
});
</script>

<template>
  <slot />
</template>
